import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { FieldTypeEnum } from '@app/core/enums/field-type-enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { ValidatorType } from '@app/core/enums/validator-type.enum';
import { isValidatorFieldDependant } from '@app/core/model/other/field-config';
import { FieldValidator } from '@app/core/model/other/field-validator';
import { AbstractFieldBuilder } from '@app/shared/components/fields/abstract.field';
import { format } from '@app/shared/extra/decimal-format';
import { getValue } from '@app/shared/extra/utils';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';


@Component({
  selector: 'number-field-builder',
  templateUrl: './number-field-builder.component.html',
  styleUrls: ['./number-field-builder.component.scss'],
  standalone: false
})
export class NumberFieldBuilderComponent extends AbstractFieldBuilder<FormControl<number>> implements OnInit, AfterViewInit {
  public Permission = PermissionEnum;

  @ViewChild('inputField') public inputField: ElementRef;

  public ngOnInit(): void {
    this.form = new FormGroup({
      field: new FormControl(
        this.fieldInitValue,
        this.computeValidators()
      )
    });

    this.form.updateValueAndValidity({emitEvent: false});
    // Initialise the field in the registry
    this.setFieldValue(this.fieldInitValue);
    this.setFieldInitialValue(this.fieldInitValue);
    this.getNextState();
  }

  public ngAfterViewInit(): void {
    this.setupHooks();

    this.form.controls.field.valueChanges.pipe(
      distinctUntilChanged(),
      debounceTime(50),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((value) => {
      this.setFieldValue(value ? format(value) : value);

      // When other field value changes, tell anything hooked to the valuechange to update accordingly
      if (this.formStateService.getPath(['gteField', this.fieldConfig.fieldCode])) {
        this.formStateService.getPath(['gteField', this.fieldConfig.fieldCode]).next(value);
      }
      if (this.formStateService.getPath(['lteField', this.fieldConfig.fieldCode])) {
        this.formStateService.getPath(['lteField', this.fieldConfig.fieldCode]).next(value);
      }
      if (this.formStateService.getPath(['computed'])) {
        this.formStateService.getPath(['computed']).next(value);
      }
    });
  }

  public cancel(): void {
    this.form.controls.field.setValue(this.getFieldInitialValue());
    super.cancel();
  }

  /**
   * Prevents typing unwanted characters in a numeric field
   * @param {InputEvent} event
   * @returns {boolean}
   */
  public validateKeypressEvent(event: InputEvent): boolean {
    const input = event.data;
    const pattern = /[0-9\-.,]/;
    return event.inputType != 'insertText' || this.validateInput(input, pattern);
  }

  /**
   * Prevents pasting unwanted characters in a numeric field
   * @param {ClipboardEvent} event
   * @returns {boolean}
   */
  public validatePasteEvent(event: ClipboardEvent): boolean {
    const input = event.clipboardData.getData('text');
    const pattern = /^(?:-?\d+|-?\d{1,3}(?:\s?\d{3})+)?(?:[,.]\d+)?$/;
    return this.validateInput(input, pattern);
  }

  /**
   * Set focus on the element
   */
  public focus(): void {
    if (this.focusable) {
      setTimeout(() => this.inputField.nativeElement.focus());
    }
  }

  protected setupHooks(): void {
    super.setupHooks();
    this.form.controls.field.setAsyncValidators(this.computeAsyncValidators());

    // Hook to the value dependant validators if they exist on this field
    const fieldDependantValidators = this.fieldConfig.field?.validators.filter((validator) => isValidatorFieldDependant(
      validator.type));
    fieldDependantValidators.forEach(fieldDependantValidator => {
      // Create a hook on the valuechange
      if (!this.formStateService.getPath([fieldDependantValidator.type, fieldDependantValidator.definitionPath.last()])) {
        this.formStateService.setPath([
          fieldDependantValidator.type,
          fieldDependantValidator.definitionPath.last()
        ], new Subject<number>());
      }

      // Update gte/lte validation when reference field is updated upstream
      const valueToCompare: number = getValue(this.entity, fieldDependantValidator.definitionPath);
      this.formStateService.getPath([fieldDependantValidator.type, fieldDependantValidator.definitionPath.last()])
        .pipe(
          startWith(valueToCompare),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe((updatedValue: number) => {
        // Keep everything but current referenced gte/lte validators
        this.fieldConfig.field.validators = this.fieldConfig.field.validators
          .filter(validator => validator.ref !== fieldDependantValidator.definitionPath.last());

        this.fieldConfig.field.validators.push(
          this.validationService.convertFieldDependantValidator(fieldDependantValidator, +updatedValue)
        );

        // Skip re-adding the fieldtype-specific validators
        this.form.controls.field.setValidators(super.computeValidators());
        this.form.controls.field.updateValueAndValidity({onlySelf: false, emitEvent: false});
        this.form.controls.field.markAsTouched({onlySelf: false});
      });
    });
  }

  /**
   * Manually add fieldtype-specific validators
   * @returns {ValidatorFn}
   */
  protected computeValidators(): ValidatorFn {
    // TODO TTT-3458 Not necessary or create field builder dedicated to YEAR fields
    if (
      this.fieldConfig.field.fieldType === FieldTypeEnum.YEAR
      //To prevent duplicate
      && this.fieldConfig.field.validators.find(validator => validator.type === ValidatorType.YEAR) === void 0
    ) {
      this.fieldConfig.field.validators.push(new FieldValidator(ValidatorType.YEAR, ''));
    }

    return Validators.compose(this.fieldConfig.field?.validators
      .map(validator => this.validationService.getValidator(validator, this.entity))
    );
  }

  private validateInput(input: string, pattern: RegExp): boolean {
    return pattern.test(input);
  }
}
